import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { AvailabilityEffect } from '../types';
import {
  ALL_DAY_MINUTES,
  browserTimezone,
  buildStartTimeIso,
  buildStartTimeIsoForDate,
  buildWeeklyRRule,
  durationFromTimes,
  type ScheduleDraft,
  todayYYYYMMDD,
  WEEKDAYS,
} from '../utils/schedule';

/** The schedule fields a saved window carries; the container composes these
 * with effect + scope (provider_id) + encounter_type_id from the current
 * picker context. */
export interface AvailabilityScheduleInput {
  rrule: string | null;
  start_time: string;
  duration_minutes: number;
  timezone: string;
}

const DEFAULT_DRAFT: ScheduleDraft = {
  days: ['MO', 'TU', 'WE', 'TH', 'FR'],
  startTime: '09:00',
  endTime: '17:00',
};

export interface AvailabilityRuleDialogProps {
  open: boolean;
  mode: 'add' | 'edit';
  effect: AvailabilityEffect;
  /** Inherited context, shown read-only — set via the pickers, not here. */
  scopeLabel: string;
  encounterTypeName: string;
  /** Seed values for edit mode; add mode uses sensible defaults. */
  initial?: ScheduleDraft;
  isSaving?: boolean;
  onClose: () => void;
  onSubmit: (schedule: AvailabilityScheduleInput) => void;
}

type Recurrence = 'weekly' | 'once';

/** Add or edit one availability window — weekly recurring or a single day.
 * The window's owner (clinic default vs a clinician) and appointment type are
 * inherited from the manager's pickers and shown read-only, so this form only
 * asks "when". */
export function AvailabilityRuleDialog({
  open,
  mode,
  effect,
  scopeLabel,
  encounterTypeName,
  initial,
  isSaving = false,
  onClose,
  onSubmit,
}: AvailabilityRuleDialogProps) {
  const [recurrence, setRecurrence] = useState<Recurrence>('weekly');
  const [days, setDays] = useState<string[]>(DEFAULT_DRAFT.days);
  const [date, setDate] = useState<string>(todayYYYYMMDD());
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState(DEFAULT_DRAFT.startTime);
  const [endTime, setEndTime] = useState(DEFAULT_DRAFT.endTime);

  // Re-seed each time the dialog opens (add → defaults, edit → the rule).
  useEffect(() => {
    if (!open) return;
    const seed = initial ?? DEFAULT_DRAFT;
    setRecurrence(seed.date ? 'once' : 'weekly');
    setDays(seed.days.length > 0 ? seed.days : DEFAULT_DRAFT.days);
    setDate(seed.date ?? todayYYYYMMDD());
    setAllDay(Boolean(seed.allDay));
    setStartTime(seed.startTime);
    setEndTime(seed.endTime);
  }, [open, initial]);

  const duration = allDay
    ? ALL_DAY_MINUTES
    : durationFromTimes(startTime, endTime);
  const scheduleChosen = recurrence === 'weekly' ? days.length > 0 : Boolean(date);
  const valid = scheduleChosen && duration > 0;
  const helperText = useMemo(() => {
    if (!scheduleChosen) {
      return recurrence === 'weekly' ? 'Pick at least one day.' : 'Pick a date.';
    }
    if (duration <= 0) return 'End time must be after start time.';
    return allDay
      ? `All day, midnight to midnight in ${browserTimezone()}.`
      : `Times are in ${browserTimezone()}.`;
  }, [scheduleChosen, recurrence, duration, allDay]);

  const noun = effect === 'open' ? 'open hours' : 'block';
  const title = `${mode === 'add' ? 'Add' : 'Edit'} ${noun}`;

  const handleSubmit = () => {
    if (!valid) return;
    const timeOfDay = allDay ? '00:00' : startTime;
    onSubmit(
      recurrence === 'weekly'
        ? {
            rrule: buildWeeklyRRule(days),
            start_time: buildStartTimeIso(timeOfDay),
            duration_minutes: duration,
            timezone: browserTimezone(),
          }
        : {
            rrule: null,
            start_time: buildStartTimeIsoForDate(date, timeOfDay),
            duration_minutes: duration,
            timezone: browserTimezone(),
          },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{title}</DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', mt: 0.5, mb: 2 }}
          useFlexGap
        >
          <Chip size="small" variant="outlined" label={scopeLabel} />
          <Chip size="small" variant="outlined" label={encounterTypeName} />
        </Stack>

        <Stack
          component="form"
          spacing={2}
          onSubmit={event => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={recurrence}
            onChange={(_event, value: Recurrence | null) => {
              if (value) setRecurrence(value);
            }}
            aria-label="Repeats"
            fullWidth
          >
            <ToggleButton value="weekly" sx={{ textTransform: 'none' }}>
              Every week
            </ToggleButton>
            <ToggleButton value="once" sx={{ textTransform: 'none' }}>
              Single day
            </ToggleButton>
          </ToggleButtonGroup>

          {recurrence === 'weekly' ? (
            <ToggleButtonGroup
              size="small"
              value={days}
              onChange={(_event, value: string[]) => setDays(value)}
              aria-label="Days of week"
              sx={{ flexWrap: 'wrap' }}
            >
              {WEEKDAYS.map(day => (
                <ToggleButton
                  key={day.code}
                  value={day.code}
                  aria-label={day.label}
                >
                  {day.short}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : (
            <TextField
              type="date"
              size="small"
              label="Date"
              value={date}
              onChange={event => setDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allDay}
                onChange={(_event, checked) => setAllDay(checked)}
              />
            }
            label="All day"
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
          />

          {!allDay && (
            <Stack direction="row" spacing={1.5}>
              <TextField
                type="time"
                size="small"
                label="Start"
                value={startTime}
                onChange={event => setStartTime(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                type="time"
                size="small"
                label="End"
                value={endTime}
                onChange={event => setEndTime(event.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                error={duration <= 0}
                fullWidth
              />
            </Stack>
          )}

          <Typography
            variant="caption"
            color={valid ? 'text.secondary' : 'error'}
            sx={{ minHeight: 18 }}
          >
            {helperText}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || isSaving}
          sx={{ textTransform: 'none' }}
        >
          {mode === 'add' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
